import AccountCircle from '@mui/icons-material/AccountCircle'
import Language from '@mui/icons-material/Language'
import Search from '@mui/icons-material/Search'
import { Stack, Typography } from '@mui/material'
import LoggedInAvatarButton from 'App/LoggedInNavigation/LoggedInAvatarButton'
import { useTrackCustomClick } from 'features/Telemetry/GoogleTagManager/Hooks/useTrackCustomClick'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'
import { useSliceStateSelector } from 'slices/hooks'
import { getLoginUrl } from '../../Header.hooks'
import { StyledSecondaryActionButton } from '../../Header.styles'
import useSelectedLanguage from '../../../hooks/useSelectedLanguage'
import LanguageChooser from 'features/LanguageChooser'

interface SecondaryActionButtonProps {
  url: string
  onClickHandler?: () => void
  title: string
  icon: JSX.Element
  testId: string
}

const SecondaryActionButton = ({
  url,
  onClickHandler,
  title,
  icon,
  testId,
}: SecondaryActionButtonProps) => {
  const trackCustomClick = useTrackCustomClick()

  return (
    <StyledSecondaryActionButton
      href={url}
      onClick={() => {
        trackCustomClick('Menu action button', {
          label: title,
          href: url,
        })

        onClickHandler?.()
      }}
      disableRipple
      key={`secondaryactionbutton-${title}`}
      data-testid={testId}
    >
      {icon}
      <Typography variant="body1">{title}</Typography>
    </StyledSecondaryActionButton>
  )
}

const SecondaryActionButtons = () => {
  const location = useLocation()
  const { isTranslationsEnabled } = useSelectedLanguage()

  const { visitorContextStatus } = useSliceStateSelector(
    (state) => state.slices.visitorContexts
  )

  const { isLoggedIn } = useSliceStateSelector((state) => state.slices.auth)
  const isSiteInEnglish = location.pathname.includes('/trr-in-english/')
  const { t } = useTranslation()

  return (
    <Stack
      flexDirection="row"
      alignItems="center"
      gap={1}
      data-test-id="scondary-action-buttons"
    >
      <SecondaryActionButton
        icon={<Search />}
        title={t('translation:common.search')}
        url="/sok"
        testId={`secondaryactionbutton-Sök`}
      />

      {isLoggedIn && <LoggedInAvatarButton key="loggedin-avatar-button" />}

      {!isLoggedIn && (
        <SecondaryActionButton
          icon={<AccountCircle />}
          title={t('translation:common.login')}
          url={getLoginUrl('/login', visitorContextStatus)}
          testId="header-avatar-button"
        />
      )}

      {isTranslationsEnabled && <LanguageChooser />}

      {!isTranslationsEnabled && (
        <SecondaryActionButton
          icon={<Language />}
          title={isSiteInEnglish ? 'På Svenska' : 'In English'}
          url={isSiteInEnglish ? '/' : '/trr-in-english/'}
          testId="secondaryactionbutton-Language"
        />
      )}
    </Stack>
  )
}

export default SecondaryActionButtons
